import {
  ArrowDownIcon,
  ArrowUpIcon,
  Collapsible,
  Typography,
} from '@atlas-design-system/react'
import { ReactNode } from 'react'

import styles from './custom-collapsible.module.css'

type CustomCollapsibleProps = {
  title: string
  icon?: ReactNode
  children: ReactNode
  defaultOpened?: boolean
  'data-testid'?: string
}

type CustomHeaderProps = {
  onClick: () => void
  tabIndex: number
  onKeyDown: () => void
  isExpanded: boolean
}

type CustomHeaderInnerProps = CustomHeaderProps & {
  title: string
  icon?: ReactNode
  testId?: string
}

interface CustomHeaderWrapperProps extends CustomHeaderProps {
  title: string
  icon?: ReactNode
  testId?: string
}

const CustomHeader: React.FC<CustomHeaderInnerProps> = ({
  onClick,
  tabIndex,
  onKeyDown,
  isExpanded,
  title,
  icon,
  testId,
}) => {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      className={`mmp-custom-collapsible-header ${styles.header}`}
    >
      <div className={styles.headerLeft}>
        {isExpanded ? <ArrowUpIcon width="16" /> : <ArrowDownIcon width="16" />}
        <div className={styles.headerTitle}>
          <Typography variant="contentText2" bold>
            {title}
          </Typography>
        </div>
      </div>
      {icon && <div className={styles.headerRight}>{icon}</div>}
    </button>
  )
}

const CustomHeaderWrapper: React.FC<CustomHeaderWrapperProps> = ({
  title,
  icon,
  testId,
  ...headerProps
}) => (
  <CustomHeader {...headerProps} title={title} icon={icon} testId={testId} />
)

type CustomCollapsibleHeaderProps = CustomHeaderProps & {
  title: string
  icon?: ReactNode
  testId?: string
}

type CollapsibleHeaderComponentProps = CustomCollapsibleHeaderProps & {
  title: string
  icon?: ReactNode
  testId?: string
}

const CollapsibleHeader: React.FC<CollapsibleHeaderComponentProps> = ({
  title,
  icon,
  testId,
  ...headerProps
}) => (
  <CustomHeaderWrapper
    {...headerProps}
    title={title}
    icon={icon}
    testId={testId}
  />
)

type CustomCollapsibleHeaderFactoryProps = {
  title: string
  icon?: ReactNode
  testId?: string
}

const createCollapsibleHeader = (
  props: CustomCollapsibleHeaderFactoryProps,
) => {
  const { title, icon, testId } = props
  return (headerProps: CustomHeaderProps) => (
    <CollapsibleHeader
      {...headerProps}
      title={title}
      icon={icon}
      testId={testId}
    />
  )
}

export const CustomCollapsible: React.FC<CustomCollapsibleProps> = ({
  title,
  icon,
  children,
  defaultOpened = false,
  ...rest
}) => {
  return (
    <Collapsible
      className={styles.wrapper}
      defaultOpened={defaultOpened}
      header={createCollapsibleHeader({
        title,
        icon,
        testId: rest['data-testid'],
      })}
    >
      <div className={`mmp-custom-collapsible-content ${styles.content}`}>
        {children}
      </div>
    </Collapsible>
  )
}
